import { atomWithStorage } from "jotai/utils";

/**
 * An in-flight bridge deposit (Step 2 of the deposit dialog). The ERC-20
 * transfer to the Hyperliquid bridge resolves on *acceptance*, but the funds
 * take ~1 min to be credited to the perp trading account, so the dialog
 * persists the deposit across dialog close / page refresh to keep showing the
 * "crediting" state until it lands.
 *
 * There is no Hyperliquid API for bridge-credit status, so arrival is detected
 * purely by the trading balance rising above `balanceBefore` (the balance
 * captured at submit time). Watching for *any* increase — rather than an
 * absolute `>= amount` target — keeps detection robust to concurrent balance
 * changes (e.g. open-position PnL) and to the pre-deposit balance.
 *
 * Lifecycle: in-flight ("crediting") → on arrival the record is stamped with
 * `completedAt` and kept for a short "completed" check window
 * (`PENDING_DEPOSIT_COMPLETED_VISIBLE_MS`) so indicators can flash a done
 * state — then deleted. The sole writer is deposit-dialog's lifecycle effect.
 *
 * Mirror of `stores/pending-withdrawal.ts`, watching the trading balance
 * instead of the on-chain wallet balance. The three pure helpers below are
 * intentionally duplicated rather than shared — de-dup into a generic
 * primitive only if a third fund-flow needs it. (YAGNI)
 */
export type PendingDeposit = {
	/** USDC amount bridged — recorded for reference / preview only. */
	amount: string;
	/** Trading balance at submit time — the arrival-detection baseline. */
	balanceBefore: number;
	/** Epoch ms when the bridge transfer was submitted (for TTL expiry). */
	ts: number;
	/** Bridge tx hash — restores the Arbiscan link when the dialog reopens. */
	txHash?: string | null;
	/** Epoch ms when arrival was first observed — starts the "completed" check window. */
	completedAt?: number;
};

/** Per-address record of an in-flight deposit, keyed by lowercased address. */
export const pendingDepositsAtom = atomWithStorage<
	Record<string, PendingDeposit>
>("trade_pending_deposits_v1", {});

/**
 * Auto-expire a stuck record (a deposit that never visibly credited). Must
 * exceed the account REST backstop interval (600s) so the failsafe never
 * force-clears before the slowest legitimate detection path (a REST refresh).
 */
export const PENDING_DEPOSIT_TTL_MS = 15 * 60_000;

/** Float guard for the "balance rose above baseline" arrival check. */
export const PENDING_DEPOSIT_EPSILON = 0.000001;

/** How long the post-arrival "completed" check stays visible before the record is dropped. */
export const PENDING_DEPOSIT_COMPLETED_VISIBLE_MS = 10_000;

/** True once the trading balance has risen above the submit-time baseline. */
export function pendingDepositArrived(
	record: PendingDeposit,
	tradingBalance: number,
): boolean {
	return tradingBalance > record.balanceBefore + PENDING_DEPOSIT_EPSILON;
}

/** True once the record is older than the TTL (failsafe cleanup). */
export function pendingDepositExpired(
	record: PendingDeposit,
	now: number,
): boolean {
	return now - record.ts >= PENDING_DEPOSIT_TTL_MS;
}

/**
 * A deposit is "in transit" while a record exists, has not expired, and the
 * trading balance has not yet risen above the submit-time baseline.
 */
export function pendingDepositInTransit(
	record: PendingDeposit | undefined,
	tradingBalance: number,
	now: number,
): boolean {
	if (!record) return false;
	// Once completed, never "in transit" again — even if the balance dips back
	// below the baseline during the check window (e.g. an immediate trade/loss).
	if (record.completedAt !== undefined) return false;
	return (
		!pendingDepositExpired(record, now) &&
		!pendingDepositArrived(record, tradingBalance)
	);
}

/** True while the post-arrival "completed" check should be visible. */
export function pendingDepositJustCompleted(
	record: PendingDeposit | undefined,
	now: number,
): boolean {
	if (record?.completedAt === undefined) return false;
	return now - record.completedAt < PENDING_DEPOSIT_COMPLETED_VISIBLE_MS;
}
